import React, {useCallback, useContext} from 'react';
import removeShortcutMutation from './graphql/remove-shortcut.graphql';
import addShortcutMutation from './graphql/add-shortcut.graphql';
import loginMutation from './graphql/login.graphql';
import logoutMutation from './graphql/logout.graphql';
import twoFactorLogin from './graphql/two-factor-login.graphql';
import updateOptionsMutation from './graphql/update-options.graphql';
import addAuthenticatorMutation from './graphql/add-authenticator.graphql';
import twoFactorResetMutation from './graphql/two-factor-reset.graphql';
import emailLoginMutation from './graphql/email-login.graphql';
import emailLoginRequestMutation from './graphql/email-login-request.graphql';
import {
  Query,
  Mutation,
  User,
  usePingQueryQuery,
  useAddShortcutMutationMutation,
  useRemoveShortcutMutationMutation,
  useLoginMutationMutation,
  useLogoutMutationMutation,
  useUpdateOptionsMutationMutation,
  useTwoFactorLoginMutation,
  useAddAuthenticatorMutation,
  useTwoFactorResetMutation,
  useEmailLoginMutation,
  useEmailLoginRequestMutation,
  useGetMeQueryQuery,
  TwoFactorResetMutationResult,
} from '../generated/graphql';
import {useQuery, useMutation, useApolloClient, from, FetchResult} from '@apollo/client';
import { useState, useEffect } from 'react';
//import { onError } from '@apollo/client/link/error';
//import { pingQuery } from '../graphql';
import {useSnackbar} from "notistack";
import {Refresh} from "@mui/icons-material";
import { Button } from '@mui/material';
import {onError} from "@apollo/client/link/error";

export const UserContext = React.createContext(null);

export function useUserContext(): UserContextValue {
  return useContext<UserContextValue>(UserContext);
}


interface UserContextValue {
  user: User | undefined;
  cookie: string|undefined;
  addToShortcuts: (id) => Promise<FetchResult<Mutation>>;
  removeFromShortcuts: (id) => Promise<FetchResult<Mutation>>;
  setOption: (name, value, searchName) => null;
  setDelete: (name, id) => null;
  getOption: (name,id,isSearch) => (null | any);
  hasOption: (name)=>boolean;
  isError: boolean;
  errorMessage: string|null;
  isLoading: boolean;
  isLoggedIn: boolean|null;
  doLogin: ({username, password}: { username: string; password: string }) => Promise<void>;
  doLogout: () => Promise<void>;
  hasPermission: (code) => (boolean);
  hasAuthenticatorCode: boolean;
  getAuthenticatorCode: (callback) => Promise<void>;
  do2FA: (code) => Promise<void>;
  setMarked2Fa: (value) => void;
  emailLogin: (code) => Promise<void>;
  reset2Fa: (callback) => Promise<void>;
  emailLoginRequest: (callback) => Promise<void>;
  activeChallengeCountInbound:number;
  activeChallengeCountOutbound:number;
  challengeCountInboundNew: number;
  challengeCountOutboundNew: number;
  setUserNotifications: (notifications: Array<Notification>)=>void;
  setActiveChallengeCounts: (any)=>void;
}

export const UserProvider = ({ children }) => {
  const [jsVersion,setJsVersion] = useState<number|null|undefined>(0);
  const [addShortcut] = useAddShortcutMutationMutation();
  const [removeShortcut] = useRemoveShortcutMutationMutation();
  const [login,loginObject] = useLoginMutationMutation();
  const [logout] = useLogoutMutationMutation();
  const [updateOptions] = useUpdateOptionsMutationMutation();
  const [twoFactorMutation,twoFactorMutationObject] = useTwoFactorLoginMutation();
  const [addAuthenticator,addAuthenticatorObject] = useAddAuthenticatorMutation();
  const [marked2Fa,setMarked2Fa] = useState(false);
  const [twoFactorResetFunc,twoFactorResetObject] = useTwoFactorResetMutation();
  const [emailLoginFunc,emailLoginObject] = useEmailLoginMutation();
  const [emailLoginRequestFunc,emailLoginRequestObject] = useEmailLoginRequestMutation();
  const [wasError,setWasError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [permissions,setPermissions] = useState({});
  const [localData,setLocalData] = useState({
    activeChallengeCountInbound:0,
    activeChallengeCountOutbound:0,
    challengeCountInboundNew:0,
    challengeCountOutboundNew:0,
    messages:[]});
  const setActiveChallengeCounts = useCallback((newValues)=>{
    setLocalData((currentData)=>{
      const p = {...currentData};
      p.activeChallengeCountInbound = newValues.activeChallengeCountInbound;
      p.activeChallengeCountOutbound = newValues.activeChallengeCountOutbound;
      p.challengeCountInboundNew = newValues.challengeCountInboundNew;
      p.challengeCountOutboundNew = newValues.challengeCountOutboundNew;
      return p;
    });
  },[]);
  const setUserNotifications = useCallback((newValues)=>{
    // this is called mostly from the websocket
    // takes an array of updated values, recalculates the number for inbound/outbound yellow badges
    setLocalData((currentData)=>{
      const p = {...currentData};
      p.challengeCountOutboundNew = 0;
      p.challengeCountInboundNew = 0;
      p.messages = [...currentData.messages];
      newValues.forEach((newValue)=>{
        let theIdx = -1;
        p.messages.find((v,idx)=>{if(v.id==newValue.id) { theIdx = idx; return true; } return false; });
        if(theIdx>-1) {
          p.messages[theIdx] = newValue;
        } else {
          p.messages.push(newValue);
        }
      });
      console.log('User notifications are',p,'from',newValues);
      // recalculate the numbers
      p.messages.forEach((v)=>{
        switch(v.type) {
          case 'challenge_received_own':
            if(v.status!='viewed-challenge') {
              p.challengeCountInboundNew++;
            }
          break;
          case 'challenge_updated_own':
            if(v.status!='viewed-challenge') {
              p.challengeCountOutboundNew++;
            }
          break;
        }
        
      });
      console.log('SUNP',p);
      return p;
    });
  },[]);
  const { enqueueSnackbar } = useSnackbar();
  const ping = usePingQueryQuery();
  const apolloClient = useApolloClient();
  if(ping.data?.ping) {
    if(!jsVersion) {
      setJsVersion(ping.data.ping.JSVersion);
    } else {
      if(jsVersion != ping.data.ping.JSVersion) {
        enqueueSnackbar('A new version of Daybook is available, please save any outstanding changes then refresh',{
          variant:"warning",
          key:'DB-UPDATE',
          action:<Button size="small" onClick={()=>{ document.location.reload(true);} }><Refresh/>Refresh now</Button>,
          preventDuplicate: true,
          persist: true
        });
      }
    }
  }
  console.log('site version',jsVersion);

  useEffect(()=>{
    console.log('LINK CHANGE');
    apolloClient.setLink(from([
      onError((errors)=> {
        console.log('LINK ERROR',errors);
        if(!errors.graphQLErrors || !errors.graphQLErrors[0]) { return; }
        if(errors.graphQLErrors[0].message!='Unauthenticated') {
        }
      }),
      apolloClient.link]));
    const pingFunction = ()=>{
      ping.refetch();
      setTimeout(pingFunction,30000);
    };
    setTimeout(pingFunction,30000);
  },[]);

  const getMe = useGetMeQueryQuery({fetchPolicy: "network-only", errorPolicy: 'none',onError:(e)=>{ setWasError(e); },onCompleted:()=>{setWasError(null);}});
  console.log('re-render with',getMe,wasError);

  useEffect(() => {
    if(getMe?.data?.me) {
      const newPermissions = {};
      const rawPermissions = getMe.data.me?.role?.permissions;
      rawPermissions?.forEach((v)=>{
        if(!v) return;
        newPermissions[v.code??''] = true;
        if(v.enable_section) {
          newPermissions['section.'+v.enable_section] = true;
        }
      });
      setPermissions(newPermissions);
      setUserNotifications(getMe.data.me?.userNotifications);
      setActiveChallengeCounts(getMe.data.me);
    } else {
      setPermissions({});
    }
    if(!getMe?.loading) {
      setIsLoggedIn(!!getMe?.data?.me);
    }
    console.log('toggling logged in by',getMe.data,'to',!!getMe?.data?.me);
  }, [getMe.loading,getMe?.data?.me]);

  const addToShortcuts = (id) => {
    return addShortcut({ variables: { id: id } });
  };
  const removeFromShortcuts = (id) => {
    return removeShortcut({ variables: { id: id } });
  };

  const setMarked2FaWrapper = useCallback((value) => {
    getMe.refetch();
    setMarked2Fa(value);
  },[]);

  const doLogin = useCallback(async ({ username, password }) => {
    if(loginObject.loading) return;
    enqueueSnackbar('Logging in',{key:'LOGIN_START',variant:'default'});
    try {
      await login({ variables: { username: username, password: password } });
      // this returns a TOKEN, we need to fetch the user details separately...
      try {
        await getMe.refetch();
        setWasError(null);
        enqueueSnackbar('Logging in',{key:'LOGIN_END',variant:'success'});
      } catch (e) {
        setWasError(e);
        if (e.message == '2FA-REQUIRED') {
          enqueueSnackbar('Logging succeeded, proceed to 2FA',{key:'LOGIN_2FA',variant:'success'});
        } else {
          enqueueSnackbar(e.message,{key:'LOGIN_FAIL',variant:'error'});
        }
      }
    } catch (e) {
      enqueueSnackbar(e.message=='invalid_grant'?'Login Failed':e.message,{key:'LOGIN_FAIL',variant:'error'});
    }
  }, [getMe.data?.me, login,loginObject.loading]);
  
  const doLogout = useCallback(async () => {
    console.log('UserProvider - logging out');
    await logout();
    enqueueSnackbar("Logged out",{key:'LOGOUT',variant:'success'});
    // fetch the busted user details /shrug
    getMe.refetch();
  }, [getMe.data?.me, logout]);


  // const getOption = useCallback((name) => {
  //   if (!isLoggedIn) return null;
  //   let value = null;
  //   const user = getMe.data.me;
  //   user.options.forEach((v) => {
  //     if (v.name === name) {
  //       value = v.value;
  //       console.log('found?',value);
  //     }
  //   }
  //   );
  //   if (!value) { return value; }
  //   return JSON.parse(value);
  // }, [getMe.data?.me, isLoggedIn]);
  const hasOption = useCallback((name:string):boolean => {
    if (!isLoggedIn) return false;
    const user = getMe?.data?.me;
    if(!user?.options) return false;
    return !!user?.options?.find((v) => v?.name==name);
  },[getMe.data?.me, isLoggedIn]);

  const getOption = useCallback((name, selectedId,isSearch=false) => {
    // options are currently only used for search prefs and have a specialized meaning
    if (!isLoggedIn) return null;
    const user = getMe?.data?.me;
    if(!user?.options) return null;
    if(isSearch) {
      let value:string|null|undefined = null;
      let searchName:string|null|undefined = null;
      let searchNameConcat:string|null = null;
      const searchNameArray: any[] = [];
      console.log("user?",user!.options);
      // Pop up the search dropdown
      user.options.forEach((v) => {
        if (v?.name === name) {
          searchNameConcat = v?.id + '.' + v?.search_name;
          searchNameArray.push(searchNameConcat);
        }
      });
      const returnValue = {value:null,searchNameArray:searchNameArray,searchName:searchName };
      // Find the option based on the selectedId
      if (selectedId) {
        const selectedOption = user.options.find((option) => option?.id === selectedId);
        if(!selectedOption) return returnValue;
        value = selectedOption.value;
        searchName = selectedOption.search_name;
      } else {
        // this will default to the first option, which is no longer intended (2025-06-24)
        /*user?.options?.forEach((v) => {
          if (v?.name === name) {
            value = v?.value;
            searchName = v?.search_name;
          }
        }
        );*/
        // instead add a special case of 'default' which performs the same action
        const selectedOption = user.options.find((option)=>option?.search_name=='default');
        if(!selectedOption) return returnValue;
        value = selectedOption.value;
        searchName = selectedOption.search_name;
      }
      returnValue.value = value?JSON.parse(value):null;
      return returnValue;
    } else {
      return null; // meh?
    }
  }, [getMe.data?.me, isLoggedIn]);

  const do2Fa = useCallback(async (code) => {
    if(twoFactorMutationObject.loading) return;
    try {
      const result = await twoFactorMutation({ variables: { code: code } });
      if(!result?.data?.twoFactorLogin?.success) {
        throw 'Fail';
      } else {
        await getMe.refetch();
      }
    } catch (e) {
      console.log('error is',e);
      enqueueSnackbar('2 Factor login incorrect',{variant:'error'});
    }
    // this returns a TOKEN, we need to fetch the user details separately...
  }, [twoFactorMutationObject.loading]);

  // generates a new authenticator code
  const getAuthenticatorCode = useCallback(async (callback) => {
    if(addAuthenticatorObject.loading) return;
    const data = await addAuthenticator();
    callback(data?.data?.addAuthenticatorCode?.code);
  }, [addAuthenticatorObject.loading]);

  // sends a code to the users's email
  const emailLoginRequest = useCallback(async (callback) => {
    if(emailLoginRequestObject.loading) return;
    const data = await emailLoginRequestFunc();
    callback(data?.data?.emailLoginRequest);
  }, [emailLoginRequestObject.loading]);

  // confirms whether the code sent by email is correct
  const emailLogin = useCallback(async (code) => {
    if(emailLoginObject.loading) return;
    try {
      await emailLoginFunc({ variables: { code: code } });
      await getMe.refetch();
    } catch (e) {
      enqueueSnackbar('2 Factor login incorrect',{key:'LOGIN_2FA_FAIL',variant:'error'});
    }
  }, [emailLoginObject.loading]);

  // generates a reset code
  const reset2Fa = useCallback(async (callback) => {
    if(twoFactorResetObject.loading) return;
    let data:any|null = null;
    try {
      data = await twoFactorResetFunc();
    } catch (e) {
      enqueueSnackbar('Unable to generate reset email',{key:'LOGIN_2FA_FAIL',variant:'error'});
      return;
    }
    callback(data?.data?.twoFactorReset);
  }, [twoFactorResetObject.loading]);

  const setOption = useCallback(async (name, value, searchName, setToastMessage, setOpenSnackbar) => {
    const send:{update:any[],create:any[]} = { update: [], create: []};
    if (!isLoggedIn) return null;
    const user = getMe?.data?.me;
    if(!user?.options) return;
    const existingOption = user.options.find((option) => option?.search_name === searchName);
    console.log("existing record", existingOption);
    
    try {
      if (existingOption) {
        // Option with the given name already exists, update it
        const updatedOption = {
          id: existingOption.id,
          name: name,
          value: JSON.stringify(value),
          search_name: searchName
        };
        send.update.push(updatedOption);
        setToastMessage('Search updated successfully');
        setOpenSnackbar(true);
      } 
      else {
        // Option with the given name doesn't exist, create a new one
        const newOption = {
          name: name,
          value: JSON.stringify(value),
          search_name: searchName
        };
        send.create.push(newOption);
        setToastMessage('Search saved successfully');
        setOpenSnackbar(true);
      }
      const response = await updateOptions({ variables: { options: send } });
      return response;
    } catch (error) {
      console.error("Error updating options:", error);
    }
  }, [getMe.data?.me, isLoggedIn, updateOptions]);

  // Delete searches using option delete
  const setDelete = useCallback(async(name, id, setOpenSnackbar, setToastMessage) => {
      const send:{delete:string[]} = {delete: [] };
      const user = getMe?.data?.me;
      if(!user) return null;
      const existingOption = user.options?.find((option) => option?.id === id && option?.name === name);
  
      if (existingOption) {
        send.delete.push(id);
        setToastMessage('Search deleted successfully');
        setOpenSnackbar(true);
      }
      else {
        setToastMessage('Delete unsuccessful');
        setOpenSnackbar(true);
      }
      const response = await updateOptions({ variables: { options: send } });
      return response;
  }, [getMe.data?.me, isLoggedIn, updateOptions]);
  
  

  const hasPermission = useCallback((code) => {
    // disable campaign challenges 'hard'
    //if(code=='campaigns.challenges') return false;
    if (!isLoggedIn) return false;
    return !!permissions[code];
/*
    var user = getMe.data.me;
    var allowed = false;
    user.role.permissions.forEach((permission) => {
      if (permission.code === code) { allowed = true; }
    });
    return allowed;*/
  }, [permissions, isLoggedIn]);
console.log('localData',localData);
const args: UserContextValue = {
  user: getMe?.data?.me,
  cookie: getMe?.data?.me?.token,
  addToShortcuts: addToShortcuts,
  removeFromShortcuts: removeFromShortcuts,
  setOption: setOption,
  setDelete: setDelete,
  getOption: getOption,
  hasOption: hasOption,
  isError: !!getMe.error,
  errorMessage: getMe.error?.message??'',
  isLoading: getMe.loading,
  isLoggedIn: isLoggedIn,
  doLogin: doLogin,
  doLogout: doLogout,
  hasPermission: hasPermission,
  hasAuthenticatorCode: getMe.data?.me?.hasAuthenticatorQrCode||marked2Fa,
  getAuthenticatorCode: getAuthenticatorCode,
  do2FA: do2Fa,
  setMarked2Fa: setMarked2FaWrapper,
  emailLogin: emailLogin,
  reset2Fa: reset2Fa,
  emailLoginRequest: emailLoginRequest,
  activeChallengeCountInbound: localData.activeChallengeCountInbound,
  activeChallengeCountOutbound: localData.activeChallengeCountOutbound,
  challengeCountInboundNew: localData.challengeCountInboundNew,
  challengeCountOutboundNew: localData.challengeCountOutboundNew,
  setUserNotifications: setUserNotifications,
  setActiveChallengeCounts: setActiveChallengeCounts
};

  return (<UserContext.Provider
    value={args}
  >
    {children}
   
  </UserContext.Provider>
  );

};
